import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { trackPageView } from "@/services/analytics";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import { PetProvider } from "@/context/pet"; // ✅ Import unifié
import { SanitaryVisitProvider } from "@/context/providers/SanitaryVisitProvider";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import NotificationWrapper from "@/components/ui/NotificationWrapper";
import Layout from "@/components/layout/Layout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import ProtectedSubscribedRoute from "@/components/auth/ProtectedSubscribedRoute";
import ProtectedProRoute from "@/components/auth/ProtectedProRoute";
import ProtectedAdminRoute from "@/components/auth/ProtectedAdminRoute";
import { useAppInitialization } from "@/hooks/useAppInitialization";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { useDeepLinkHandler } from "@/hooks/useDeepLinkHandler";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import TosAcceptanceModal from "@/components/legal/TosAcceptanceModal";
import AnalyticsConsentBanner from "@/components/legal/AnalyticsConsentBanner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PWAUpdatePrompt from "@/components/PWAUpdatePrompt";

// Auth importé en eager pour éviter la mismatch hydration sur la page prérendue
import AuthPage from "@/pages/Auth";

// Lazy loaded pages pour optimiser le bundle
import * as LazyPages from "@/utils/lazyRoutes";
import LazyLoadFallback from "@/components/common/LazyLoadFallback";
import RedirectToHistory from "@/components/health/RedirectToHistory";
const ReviewsModeration = React.lazy(
  () => import("@/pages/admin/ReviewsModeration"),
);
const AdminShop = React.lazy(() => import("@/pages/admin/AdminShop"));

// QueryClient optimisé avec cache persistant
import {
  createOptimizedQueryClient,
  prefetchCriticalData,
} from "@/utils/optimizedQueryClient";

// QueryClient avec optimisations
const queryClient = createOptimizedQueryClient();

// Composant qui doit être à l'intérieur du Router pour useNavigate
const RouterContent = React.memo(() => {
  useDeepLinkHandler(); // Maintenant à l'intérieur du Router
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return (
    <Suspense
      fallback={
        <LazyLoadFallback message="Initialisation de l'application..." />
      }
    >
      <Routes>
        {/* Public routes */}
        <Route path="/auth" element={<AuthPage />} />
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.ResetPassword />
            </Suspense>
          }
        />
        {/* Supabase OAuth / magic-link callback — attend l'échange PKCE puis redirige */}
        <Route
          path="/auth/callback"
          element={
            <Suspense
              fallback={<LazyLoadFallback message="Connexion en cours..." />}
            >
              <LazyPages.AuthCallback />
            </Suspense>
          }
        />

        {/* Page publique QR code — accessible sans login */}
        <Route
          path="/animal/:publicId"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.PublicPetProfile />
            </Suspense>
          }
        />

        {/* PWA Share Target — accessible sans login (SW redirige ici après POST) */}
        <Route
          path="/share-receiver"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.ShareReceiver />
            </Suspense>
          }
        />

        {/* Legal pages — public */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.PrivacyPolicy />
            </Suspense>
          }
        />
        <Route
          path="/tos"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.TermsOfService />
            </Suspense>
          }
        />
        <Route
          path="/legal"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.LegalMentions />
            </Suspense>
          }
        />
        <Route
          path="/refund-policy"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.RefundPolicy />
            </Suspense>
          }
        />
        <Route
          path="/welcome"
          element={
            <Suspense fallback={<LazyLoadFallback message="Chargement..." />}>
              <LazyPages.Welcome />
            </Suspense>
          }
        />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <LazyLoadFallback message="Chargement de la page..." />
                    }
                  >
                    <Routes>
                      <Route path="/" element={<LazyPages.Index />} />
                      <Route path="/pets" element={<LazyPages.Pets />} />
                      <Route path="/pets/add" element={<LazyPages.AddPet />} />
                      <Route
                        path="/pets/:id/edit"
                        element={<LazyPages.EditPet />}
                      />
                      <Route
                        path="/pets/:id"
                        element={<LazyPages.PetDetails />}
                      />

                      {/* Pet detail sub-routes */}
                      <Route
                        path="/pets/:id/photos"
                        element={<LazyPages.PetPhotos />}
                      />
                      <Route
                        path="/pets/:id/notes"
                        element={<LazyPages.PetNotes />}
                      />
                      <Route
                        path="/pets/:id/locations"
                        element={<LazyPages.PetLocations />}
                      />
                      <Route
                        path="/pets/:id/documents"
                        element={<LazyPages.VeterinaryDocuments />}
                      />
                      <Route
                        path="/pets/:id/competitions"
                        element={<LazyPages.Competitions />}
                      />
                      <Route
                        path="/pets/:id/gps"
                        element={<LazyPages.PetGPSTrackers />}
                      />

                      <Route path="/profile" element={<LazyPages.Profile />} />
                      <Route path="/health" element={<LazyPages.Health />} />

                      {/* Health sub-routes */}
                      <Route
                        path="/health/vaccines"
                        element={<LazyPages.VaccinesList />}
                      />
                      <Route
                        path="/health/vaccines/add"
                        element={<LazyPages.AddVaccine />}
                      />
                      <Route
                        path="/health/vaccines/:id"
                        element={<RedirectToHistory />}
                      />
                      <Route
                        path="/health/medications"
                        element={<LazyPages.MedicationsList />}
                      />
                      <Route
                        path="/health/medications/add"
                        element={<LazyPages.AddMedication />}
                      />
                      <Route
                        path="/health/medications/:id"
                        element={<RedirectToHistory />}
                      />
                      <Route
                        path="/health/deworming"
                        element={<LazyPages.DewormingList />}
                      />
                      <Route
                        path="/health/deworming/add"
                        element={<LazyPages.AddDeworming />}
                      />
                      <Route
                        path="/health/deworming/:id"
                        element={<RedirectToHistory />}
                      />
                      <Route
                        path="/health/hygiene"
                        element={<LazyPages.HygienePage />}
                      />
                      <Route
                        path="/health/hygiene/add"
                        element={<LazyPages.AddHygienePage />}
                      />
                      <Route
                        path="/health/hygiene/:id"
                        element={<RedirectToHistory />}
                      />

                      {/* Health tracking routes */}
                      <Route
                        path="/health/weight"
                        element={<LazyPages.WeightTracking />}
                      />
                      <Route
                        path="/health/activity"
                        element={<LazyPages.ActivityTracking />}
                      />
                      <Route
                        path="/health/activity/add"
                        element={<LazyPages.AddActivity />}
                      />
                      <Route
                        path="/health/activity/:id"
                        element={<RedirectToHistory />}
                      />
                      <Route
                        path="/health/food"
                        element={<LazyPages.FoodTracking />}
                      />

                      {/* Exotic health routes */}
                      <Route
                        path="/exotic-health"
                        element={<LazyPages.ExoticHealth />}
                      />
                      <Route
                        path="/exotic-health/temperature"
                        element={<LazyPages.TemperatureHistory />}
                      />
                      <Route
                        path="/exotic-health/temperature/add"
                        element={<LazyPages.AddTemperature />}
                      />
                      <Route
                        path="/exotic-health/temperature/tracking"
                        element={<LazyPages.TemperatureTracking />}
                      />
                      <Route
                        path="/exotic-health/humidity"
                        element={<LazyPages.HumidityHistory />}
                      />
                      <Route
                        path="/exotic-health/humidity/add"
                        element={<LazyPages.AddHumidity />}
                      />
                      <Route
                        path="/exotic-health/humidity/tracking"
                        element={<LazyPages.HumidityTracking />}
                      />

                      {/* Health info routes */}
                      <Route
                        path="/health/info"
                        element={<LazyPages.PracticalInfo />}
                      />
                      <Route
                        path="/health/info/insurance"
                        element={<LazyPages.InsuranceInfo />}
                      />
                      <Route
                        path="/health/insurances"
                        element={<LazyPages.InsurancesList />}
                      />
                      <Route
                        path="/health/info/trainer"
                        element={<LazyPages.TrainerInfo />}
                      />

                      {/* Veterinarians routes */}
                      <Route
                        path="/health/vets"
                        element={<LazyPages.VetsPage />}
                      />
                      <Route
                        path="/find-veterinarian"
                        element={<LazyPages.FindVeterinarian />}
                      />

                      {/* Subscription routes */}
                      <Route
                        path="/subscription"
                        element={<LazyPages.Subscription />}
                      />
                      <Route
                        path="/subscription/upgrade"
                        element={<LazyPages.SubscriptionUpgrade />}
                      />

                      {/* Admin routes */}
                      <Route
                        path="/admin/metrics"
                        element={<LazyPages.AdminMetrics />}
                      />
                      <Route
                        path="/admin/reviews"
                        element={<ReviewsModeration />}
                      />
                      <Route
                        path="/admin/shop"
                        element={
                          <ProtectedAdminRoute>
                            <AdminShop />
                          </ProtectedAdminRoute>
                        }
                      />

                      <Route path="/gallery" element={<LazyPages.Gallery />} />
                      <Route
                        path="/calendar"
                        element={<LazyPages.Calendar />}
                      />
                      {/* Alias au singulier → redirige vers l'annuaire (évite un 404) */}
                      <Route
                        path="/breeder"
                        element={<Navigate to="/breeders" replace />}
                      />
                      <Route
                        path="/breeders"
                        element={<LazyPages.BreedersList />}
                      />
                      <Route
                        path="/breeders/:id"
                        element={<LazyPages.BreederDetail />}
                      />
                      <Route
                        path="/breeder/edit"
                        element={
                          <ProtectedProRoute>
                            <LazyPages.BreederEdit />
                          </ProtectedProRoute>
                        }
                      />
                      <Route
                        path="/breeder/dashboard"
                        element={
                          <ProtectedProRoute>
                            <LazyPages.BreederDashboard />
                          </ProtectedProRoute>
                        }
                      />
                      <Route path="/shop" element={<LazyPages.Shop />} />

                      {/* Lost Pets routes */}
                      <Route
                        path="/lost-pets"
                        element={<LazyPages.LostPets />}
                      />
                      <Route
                        path="/lost-pets/create"
                        element={<LazyPages.CreateLostPet />}
                      />
                      <Route
                        path="/lost-pets/:id"
                        element={<LazyPages.LostPetDetails />}
                      />

                      {/* Social & Community routes */}
                      <Route
                        path="/social"
                        element={
                          <ProtectedSubscribedRoute>
                            <LazyPages.SocialForum />
                          </ProtectedSubscribedRoute>
                        }
                      />
                      <Route
                        path="/insurance"
                        element={<LazyPages.InsuranceComparison />}
                      />
                      <Route
                        path="/reminders"
                        element={<LazyPages.SmartReminders />}
                      />
                      <Route
                        path="/ai-assistant"
                        element={
                          <ProtectedSubscribedRoute>
                            <LazyPages.AIAssistant />
                          </ProtectedSubscribedRoute>
                        }
                      />

                      {/* Global GPS and Competitions routes - Protected */}
                      <Route
                        path="/gps-tracking"
                        element={
                          <ProtectedSubscribedRoute>
                            <LazyPages.GPSTracking />
                          </ProtectedSubscribedRoute>
                        }
                      />
                      <Route
                        path="/competitions"
                        element={
                          <ProtectedProRoute>
                            <LazyPages.Competitions />
                          </ProtectedProRoute>
                        }
                      />

                      <Route path="*" element={<LazyPages.NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
});

const AppContent = React.memo(() => {
  useAppInitialization();
  usePerformanceMonitor(); // Monitoring des performances activé
  useOfflineSync(); // Flush queued writes when back online

  useEffect(() => {
    // Pre-fetch des données critiques en arrière-plan
    prefetchCriticalData(queryClient);
  }, []);

  return (
    <Router>
      {/* PetProvider ici : useNavigate() requiert d'être à l'intérieur du Router */}
      <PetProvider>
        <RouterContent />
        <TosAcceptanceModal />
        <AnalyticsConsentBanner />
        <Toaster />
        <SonnerToaster richColors position="top-center" />
        <NotificationWrapper />
        <PWAInstallPrompt />
        <PWAUpdatePrompt />
      </PetProvider>
    </Router>
  );
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SanitaryVisitProvider>
            <AppContent />
          </SanitaryVisitProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
